import { Capacitor } from '@capacitor/core'
import FileFormats from '../../constants/FileFormats'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import { api, Api } from '../../services/api'
import { ILabelDataHandler } from './ILabelDataHandler'

export class LabelApiDataHandler implements ILabelDataHandler {
  api: Api
  _resource = 'sra_order_labels'
  _format: FileFormats

  constructor() {
    this.api = api
    this._format = Capacitor.isNativePlatform() ? FileFormats.ZPL : FileFormats.PDF
  }

  async getDocumentBase64ByLabel(orderId: number, label: string, itemId: number): Promise<{fileContent: string, fileType: FileFormats}> {
    const { data: {file:fileContent} } = await this.api.client.get<{ file: string}>(`${this._resource}/${orderId}?label=${label}&item_ids=${itemId}&format=${this._format}`)

    return {fileContent, fileType: this._format}
  }

  create(product: OrderProduct): Promise<string> {
    return this.createBatch([product])
  }

  async createBatch(products: OrderProduct[]): Promise<string> {
    const { data: { label } } = await this.api.client.post<{label: string}>(
      this._resource,
      this._composeCreateRequestData(products)
    )

    return label
  }

  remove(product: OrderProduct): Promise<void> {
    return this.removeBatch([product])
  }

  removeBatch(products: OrderProduct[]): Promise<void> {
    const orderId = this._composeOrderId(products)
    return this.api.client.put(`${this._resource}/${orderId}`, this._composeRemoveRequestData(products))
  }

  _composeOrderId(products: OrderProduct[]): number {
    if (!products.length) {
      throw new Error('No products specified for the request')
    }

    const orderId = products[0].order?.orderId

    if (!orderId) {
      throw new Error(`Orphan product was found: product with id ${products[0].id} has no order`)
    }

    return orderId
  }

  _composeCreateRequestData(products: OrderProduct[]): { order_id: number, item_ids: number[] } {
    return {
      ...this._composeOrderIdRequestData(products),
      ...this._composeItemIdsRequestData(products),
    }
  }

  _composeRemoveRequestData(products: OrderProduct[]): { item_ids: number[] } {
    return {
      ...this._composeItemIdsRequestData(products),
    }
  }

  _composeOrderIdRequestData(products: OrderProduct[]): { order_id: number } {
    return {
      order_id: this._composeOrderId(products),
    }
  }

  _composeItemIdsRequestData(products: OrderProduct[]): { item_ids: number[] } {
    return {
      item_ids: products.reduce<number[]>((itemIds, { itemId }) => ([...itemIds, itemId]), []),
    }
  }
}
